// Slide 3 — Revision: Work Cycle + Cruise Config Mnemonic.
// TWO test-then-reveal questions on a single slide.
//
// slideQuizFlags semantics (passed in from the player):
//   [0] = first quiz on this slide (work cycle) answered correctly
//   [1] = second quiz (PAST mnemonic) answered correctly
//
// Phase A:  question 1 visible, both answers hidden
// Phase B:  question 1 answer revealed (work cycle diagram), question 2 visible
// Phase C:  both answers revealed
//
// The reveals are sticky — once a flag is true it stays true even when the
// lesson advances to the next narration block.
import React, { useEffect, useState } from "react";

export default function Slide03RevisionWorkCycle({ slideQuizFlags = [] }) {
  const q1Answered = !!slideQuizFlags[0];
  const q2Answered = !!slideQuizFlags[1];

  // Stagger the work cycle steps when q1 is first answered.
  const [q1Tick, setQ1Tick] = useState(0);
  useEffect(() => {
    if (q1Answered) {
      const t = setTimeout(() => setQ1Tick((n) => n + 1), 40);
      return () => clearTimeout(t);
    }
  }, [q1Answered]);

  return (
    <div className="w-full h-full relative overflow-hidden">
      {/* Header */}
      <div className="absolute top-6 left-8 right-8 flex items-baseline justify-between">
        <div className="text-[10px] sm:text-xs uppercase tracking-[0.18em] text-white/50 font-semibold">
          Revision
        </div>
        <div className="text-2xl sm:text-3xl font-heading font-bold text-white tracking-tight">
          Work Cycle &amp; Cruise Config
        </div>
      </div>

      {/* TWO COLUMNS */}
      <div className="absolute inset-x-8 top-20 bottom-6 grid grid-cols-2 gap-8">

        {/* LEFT COLUMN — Q1: work cycle */}
        <div className="flex flex-col">
          <div className="text-[10px] uppercase tracking-[0.18em] text-white/60 font-bold mb-2">
            Question 1
          </div>
          <div
            className={`text-base sm:text-lg font-heading font-semibold leading-snug tracking-tight ${
              q1Answered ? "text-white/70" : "text-white"
            }`}
          >
            What is the straight &amp; level work cycle?
          </div>

          {/* Answer reveal */}
          <div
            className="mt-6"
            style={{
              opacity: q1Answered ? 1 : 0,
              transform: q1Answered ? "translateY(0)" : "translateY(10px)",
              transition: "opacity 500ms cubic-bezier(0.22,1,0.36,1), transform 500ms cubic-bezier(0.22,1,0.36,1)",
            }}
          >
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#7BD389] font-bold mb-3">
              ✓ Answer
            </div>
            <WorkCycleDiagram active={q1Answered && q1Tick > 0} />
          </div>

          {!q1Answered && (
            <div className="mt-auto text-white/40 text-xs italic">
              Answer the question to reveal the work cycle
            </div>
          )}
        </div>

        {/* RIGHT COLUMN — Q2: PAST mnemonic.  Greyed out until Q1 is done. */}
        <div
          className="flex flex-col"
          style={{ opacity: q1Answered ? 1 : 0.35, transition: "opacity 600ms" }}
        >
          <div className="text-[10px] uppercase tracking-[0.18em] text-white/60 font-bold mb-2">
            Question 2
          </div>
          <div
            className={`text-base sm:text-lg font-heading font-semibold leading-snug tracking-tight ${
              q2Answered ? "text-white/70" : "text-white"
            }`}
          >
            What is the mnemonic for changing cruise configurations?
          </div>

          <div
            className="mt-6"
            style={{
              opacity: q2Answered ? 1 : 0,
              transform: q2Answered ? "translateY(0)" : "translateY(10px)",
              transition: "opacity 500ms cubic-bezier(0.22,1,0.36,1), transform 500ms cubic-bezier(0.22,1,0.36,1)",
            }}
          >
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#7BD389] font-bold mb-3">
              ✓ Answer
            </div>
            <PastDiagram show={q2Answered} />
          </div>

          {q1Answered && !q2Answered && (
            <div className="mt-auto text-white/40 text-xs italic">
              Answer the question to reveal the mnemonic
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- Work cycle: 4 boxes in a square arrow loop ----
const CYCLE_STEPS = [
  { label: "LOOKOUT",     color: "#FFA300" },
  { label: "ATTITUDE",    color: "#cdd9e8" },
  { label: "ATTITUDE",    color: "#cdd9e8" },
  { label: "PERFORMANCE", color: "#FFA300" },
];

function WorkCycleDiagram({ active }) {
  return (
    <div className="space-y-2">
      {CYCLE_STEPS.map((s, i) => (
        <div
          key={i}
          className="flex items-center gap-3"
          style={{
            opacity: active ? 1 : 0,
            transform: active ? "translateX(0)" : "translateX(-8px)",
            transition: `opacity 400ms cubic-bezier(0.22,1,0.36,1) ${i * 110}ms, transform 400ms cubic-bezier(0.22,1,0.36,1) ${i * 110}ms`,
          }}
        >
          <div
            className="w-8 h-8 flex items-center justify-center text-xs font-bold rounded-sm"
            style={{ background: s.color, color: "#1b3151" }}
          >
            {i + 1}
          </div>
          <div className="text-base sm:text-lg font-heading font-bold text-white tracking-wide">
            {s.label}
          </div>
          {i < CYCLE_STEPS.length - 1 && (
            <div className="text-white/40 text-xl">↓</div>
          )}
        </div>
      ))}
      {/* The "ALAP" mantra closes the loop */}
      <div
        className="flex items-center gap-3 pt-2"
        style={{
          opacity: active ? 1 : 0,
          transform: active ? "translateX(0)" : "translateX(-8px)",
          transition: `opacity 400ms cubic-bezier(0.22,1,0.36,1) ${CYCLE_STEPS.length * 110}ms, transform 400ms cubic-bezier(0.22,1,0.36,1) ${CYCLE_STEPS.length * 110}ms`,
        }}
      >
        <div className="px-3 py-1.5 rounded-sm bg-[#5AB6FF]/20 border border-[#5AB6FF]/50 text-[#5AB6FF] font-mono text-sm font-bold tracking-[0.18em]">
          ALAP
        </div>
        <div className="text-xs text-white/60 italic">
          (As Long As Possible — repeat the scan)
        </div>
      </div>
    </div>
  );
}

// ---- PAST mnemonic: arrow chain ----
const PAST = ["POWER", "ATTITUDE", "SPEED", "TRIM"];

function PastDiagram({ show }) {
  return (
    <div>
      <div className="text-3xl sm:text-4xl font-heading font-bold tracking-[0.16em] text-[#FFA300] mb-4"
        style={{
          opacity: show ? 1 : 0,
          transform: show ? "scale(1)" : "scale(0.85)",
          transition: "opacity 400ms, transform 400ms cubic-bezier(0.22,1,0.36,1)",
        }}
      >
        PAST
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {PAST.map((w, i) => (
          <React.Fragment key={w}>
            <div
              className="px-3 py-2 rounded-sm bg-white/10 border border-white/30 text-white font-heading font-semibold tracking-wide text-sm"
              style={{
                opacity: show ? 1 : 0,
                transform: show ? "translateY(0)" : "translateY(8px)",
                transition: `opacity 400ms ${i * 120 + 200}ms, transform 400ms cubic-bezier(0.22,1,0.36,1) ${i * 120 + 200}ms`,
              }}
            >
              <span className="text-[#FFA300] font-bold mr-1">{w[0]}</span>
              {w.slice(1).toLowerCase()}
            </div>
            {i < PAST.length - 1 && (
              <span
                className="text-white/40 text-xl"
                style={{
                  opacity: show ? 1 : 0,
                  transition: `opacity 400ms ${i * 120 + 260}ms`,
                }}
              >
                →
              </span>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
